/**
 * Analyzer: Day Bar Context
 *
 * Surfaces patterns that only become visible once we have actual price-action
 * data alongside the trade record. Reads `trade.barAnalysis` (populated by
 * the AddEditTradeModal's chart load — see `services/barAnalysis/maeMfe.js`).
 *
 * Three patterns this round, each with strong evidence requirements so we
 * don't burn the user's attention on weak signals:
 *
 *   1. Winner reversed — trade was meaningfully ahead in MFE, then closed
 *      at a loss, with MFE timing earlier than MAE timing (so the "winner
 *      turning loser" arc actually happened, not just simultaneous chop).
 *      WARNING severity, anchors the day's most actionable lesson.
 *
 *   2. MFE leakage — among winners, the average ratio of realized P&L to
 *      MFE is low (you exit too early). Reported only when ≥3 winners have
 *      barAnalysis (sample-size guard) and avg capture is <40%.
 *      WARNING severity.
 *
 *   3. Sharp entries — trades with both near-zero MAE and meaningful MFE.
 *      Positive reinforcement — names what the user is doing right so they
 *      can repeat it. SUCCESS severity.
 *
 * Trades without barAnalysis are silently skipped. If no trades have it,
 * the analyzer returns null (no observations) — same convention as the
 * other day analyzers when they have no signal to emit.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'dayBarContext';
export const tier = 'free';

// Thresholds — tuned to fire on patterns the trader will recognize without
// firing on every routine trade. Conservative by design; better to undershoot
// than to flood the Coach feed with weak observations.
const SHARP_ENTRY_MAX_MAE_R    = 0.25;  // ≤0.25R adverse = "barely shook"
const SHARP_ENTRY_MIN_MFE_R    = 1.5;   // ≥1.5R favorable = meaningful
const REVERSED_MIN_MFE_R       = 0.75;  // were ahead by ≥0.75R at peak
const REVERSED_MIN_MAE_R       = 0.5;   // then drawdown of ≥0.5R magnitude
const LEAKAGE_MIN_WINNERS      = 3;     // need 3 winners to compute average
const LEAKAGE_MAX_AVG_CAPTURE  = 0.4;   // captured <40% of MFE on average

// Wick stop-out window: trades stopped out where MAE was barely past 1R AND
// MFE was meaningful inside the trade window. Tight upper bound on MAE so
// we don't flag every losing trade — only those where the move barely
// "tagged" the SL before reversing.
const WICK_MAE_R_MIN           = 1.0;   // MAE must have actually breached SL distance
const WICK_MAE_R_MAX           = 1.15;  // but only barely — within 15% of stop
const WICK_MIN_MFE_R           = 0.5;   // and the trade had real upside before/after

// Counter-trend (200-EMA-relative) thresholds. Need a meaningful sample of
// counter-trend trades AND a meaningful WR gap before flagging — otherwise
// we'd cry wolf on a normal day with one or two counter-trend setups.
const COUNTERTREND_MIN_TRADES  = 3;     // need 3+ counter-trend trades
const COUNTERTREND_MIN_WR_GAP  = 0.20;  // 20pp WR gap vs trend-aligned trades

// VWAP alignment thresholds. Same shape as the 200-EMA pattern but
// independent — a trade can be VWAP-misaligned while EMA-aligned (early
// in the session before EMAs warm up, for example) or vice versa, so
// both observations can fire on the same day without contradicting.
const VWAP_MISALIGN_MIN_TRADES = 3;     // need 3+ misaligned trades
const VWAP_MISALIGN_MIN_WR_GAP = 0.20;  // 20pp WR gap to flag

// EMA stack alignment thresholds. Stack = the four EMAs in strict order:
//   bullish: ema9 > ema21 > ema50 > ema200    (textbook uptrend structure)
//   bearish: ema9 < ema21 < ema50 < ema200    (textbook downtrend structure)
//   neutral: anything else (chop, transition, mixed)
// Trades against an aligned stack are structurally fighting the trend; this
// is one of the most-watched setups in price action trading. Same threshold
// shape as the other alignment patterns.
const STACK_MIN_TRADES         = 3;
const STACK_MIN_WR_GAP         = 0.20;

// EMA pullback thresholds. A "textbook pullback" entry is one that:
//   - happens during stack alignment (with-stack direction)
//   - lands within `PULLBACK_MAX_DIST_R` of an EMA in the stack
// (i.e., the trader bought a dip to the 21 EMA in an uptrend, etc.).
// Distance is measured in R-multiples to make it instrument-agnostic.
const PULLBACK_MAX_DIST_R      = 0.3;   // entry within 0.3R of an EMA = "near"
const PULLBACK_MIN_TRADES      = 2;     // emit even on 2 — these are positive cases worth naming

/**
 * Pull a usable barAnalysis off a trade. Returns null if missing or malformed.
 */
const ba = (trade) => {
  const a = trade?.barAnalysis;
  if (!a || typeof a !== 'object') return null;
  if (!Number.isFinite(a.maePoints) || !Number.isFinite(a.mfePoints)) return null;
  return a;
};

/**
 * Trades with no realized P&L (open) or no usable barAnalysis are skipped.
 * Returns the subset of enriched trades the analyzer can reason about.
 */
const usableTrades = (enriched) => {
  return (enriched || []).filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (!Number.isFinite(t.netPnl)) return false;
    return true;
  });
};

const fmtR = (r) => {
  if (!Number.isFinite(r)) return '';
  const abs = Math.abs(r);
  return `${r >= 0 ? '+' : '−'}${abs.toFixed(2)}R`;
};

const fmtPct = (n) => {
  if (!Number.isFinite(n)) return '';
  return `${Math.round(n * 100)}%`;
};

const fmtTime = (unixSec) => {
  if (!Number.isFinite(unixSec)) return '';
  try {
    const d = new Date(unixSec * 1000);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York'
    });
  } catch { return ''; }
};

// ── Pattern 1: Winner reversed ────────────────────────────────────────────

const detectReversed = (day, trades) => {
  const reversed = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (t.netPnl >= 0) return false;                      // must be a loser
    if (!Number.isFinite(a.mfeR) || !Number.isFinite(a.maeR)) return false;
    if (a.mfeR < REVERSED_MIN_MFE_R) return false;        // was meaningfully ahead
    if (a.maeR < REVERSED_MIN_MAE_R) return false;        // experienced real drawdown
    if (!Number.isFinite(a.mfeAt) || !Number.isFinite(a.maeAt)) return false;
    return a.mfeAt < a.maeAt;                              // peak before drawdown (winner→loser arc)
  });
  if (reversed.length === 0) return null;

  // Pick the worst one for the headline (most regret-inducing — biggest peak,
  // converted into a loser). Keep all in evidence so the modal can drill in.
  const worst = [...reversed].sort((a, b) => ba(b).mfeR - ba(a).mfeR)[0];
  const wba = ba(worst);
  const total = trades.length;

  const headlineWhen = fmtTime(wba.mfeAt);
  const detailParts = [];
  if (reversed.length === 1) {
    detailParts.push(`Was ${fmtR(wba.mfeR)} at ${headlineWhen}, exited around ${fmtR(wba.maeR === 0 ? 0 : -wba.maeR)} after the reversal.`);
  } else {
    detailParts.push(`${reversed.length} of ${total} trades let an open winner reverse into a loss.`);
    detailParts.push(`Worst case: ${fmtR(wba.mfeR)} peak at ${headlineWhen}, then drawdown of ${fmtR(-wba.maeR)}.`);
  }

  return makeObservation({
    id: `${key}:${day.date}:reversed`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: reversed.length === 1
      ? 'Winner reversed into a loser'
      : `${reversed.length} winners reversed into losers`,
    detail: detailParts.join(' '),
    action: 'Pull these trades up and check what your stop-management plan said at the MFE peak — was the breakeven move triggered?',
    evidence: {
      reversedTradeIds: reversed.map(t => t.id),
      worstTradeId: worst.id,
      worstMfeR: wba.mfeR,
      worstMaeR: wba.maeR,
      worstMfeAt: wba.mfeAt,
      worstMaeAt: wba.maeAt,
    },
    confidence: 0.85,
    priority: 78
  });
};

// ── Pattern 2: MFE leakage (low capture ratio across winners) ─────────────

const detectMfeLeakage = (day, trades) => {
  // Eligible: winners with positive MFE in dollars (we use dollars to avoid
  // R-multiple/no-stop ambiguity — capture ratio is unitless either way).
  const winners = trades.filter(t => t.netPnl > 0 && Number.isFinite(ba(t)?.mfeDollars) && ba(t).mfeDollars > 0);
  if (winners.length < LEAKAGE_MIN_WINNERS) return null;

  let sum = 0;
  let worstCapture = 1;
  let worstTrade = null;
  for (const t of winners) {
    const mfeD = ba(t).mfeDollars;
    const capture = Math.max(0, Math.min(1, t.netPnl / mfeD));
    sum += capture;
    if (capture < worstCapture) {
      worstCapture = capture;
      worstTrade = t;
    }
  }
  const avgCapture = sum / winners.length;
  if (avgCapture >= LEAKAGE_MAX_AVG_CAPTURE) return null;

  const wba = worstTrade ? ba(worstTrade) : null;
  const worstClause = (worstTrade && wba && Number.isFinite(wba.mfeR))
    ? ` Biggest leak: ${fmtR(wba.mfeR)} peak captured at ${fmtPct(worstCapture)}.`
    : '';

  return makeObservation({
    id: `${key}:${day.date}:mfe-leakage`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `Captured only ${fmtPct(avgCapture)} of MFE on average`,
    detail: `Across ${winners.length} winners, the average exit took ${fmtPct(avgCapture)} of the available favorable move.${worstClause}`,
    action: 'Try a partial-exit rule: take half off at your usual target, run the rest with a trailing stop. Compare next-week capture to today.',
    evidence: {
      avgCapture,
      winnerCount: winners.length,
      worstTradeId: worstTrade?.id ?? null,
      worstCapture
    },
    confidence: 0.8,
    priority: 65
  });
};

// ── Pattern 5: Counter-trend (200 EMA) win-rate gap ──────────────────────
//
// Classifies each trade with a populated `barAnalysis.emasAtEntry[200]` as
// either trend-aligned or counter-trend, based on whether the entry price
// sits on the same side of the 200 EMA as the trade's direction:
//
//   - Long entry above 200 EMA  → trend-aligned
//   - Long entry below 200 EMA  → counter-trend
//   - Short entry below 200 EMA → trend-aligned
//   - Short entry above 200 EMA → counter-trend
//
// If the day has ≥3 counter-trend trades AND their win rate is ≥20pp lower
// than trend-aligned trades, flag it. Conservative thresholds — counter-trend
// trading isn't inherently bad, but a clear WR gap is actionable.

const detectCountertrendGap = (day, trades) => {
  // Classify every trade with a usable 200 EMA snapshot
  const classified = trades
    .map(t => {
      const a = ba(t);
      const ema200 = a?.emasAtEntry?.['200'] ?? a?.emasAtEntry?.[200];
      if (!Number.isFinite(ema200)) return null;
      const entryPx = Number(t?.entry);
      if (!Number.isFinite(entryPx)) return null;

      const isLong = t.direction !== 'short';
      const aboveEma = entryPx > ema200;
      const trendAligned = (isLong && aboveEma) || (!isLong && !aboveEma);
      return { trade: t, trendAligned, ema200, entryPx };
    })
    .filter(Boolean);

  if (classified.length === 0) return null;

  const trend       = classified.filter(c => c.trendAligned);
  const counter     = classified.filter(c => !c.trendAligned);
  if (counter.length < COUNTERTREND_MIN_TRADES) return null;
  if (trend.length === 0) return null; // need a baseline to compare

  const winRate = (group) => {
    if (group.length === 0) return 0;
    const wins = group.filter(c => c.trade.netPnl > 0).length;
    return wins / group.length;
  };
  const trendWR   = winRate(trend);
  const counterWR = winRate(counter);
  const gap = trendWR - counterWR;
  if (gap < COUNTERTREND_MIN_WR_GAP) return null;

  return makeObservation({
    id: `${key}:${day.date}:countertrend-gap`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: 'Counter-trend trades underperforming today',
    detail: `${counter.length} trades on the wrong side of the 200 EMA hit ${fmtPct(counterWR)} WR vs ${fmtPct(trendWR)} for ${trend.length} trend-aligned trades.`,
    action: 'Add a checklist item: confirm price-vs-200-EMA alignment before entry. Or reserve counter-trend setups for specific patterns (failed breakouts, mean-reversion).',
    evidence: {
      counterTradeIds: counter.map(c => c.trade.id),
      trendTradeIds: trend.map(c => c.trade.id),
      counterWR,
      trendWR,
      counterCount: counter.length,
      trendCount: trend.length,
    },
    confidence: 0.78,
    priority: 70
  });
};

// ── Pattern 7: EMA stack alignment ───────────────────────────────────────
//
// Classifies the EMA stack at trade entry into one of three states:
//
//   bullish  — 9 > 21 > 50 > 200   (textbook uptrend structure)
//   bearish  — 9 < 21 < 50 < 200   (textbook downtrend structure)
//   neutral  — anything else (chop, transition, mixed signals)
//
// A trade is "with-stack" when its direction matches the stack (long+bullish
// or short+bearish), "against-stack" when opposed, and "neutral" otherwise.
// Trades on a neutral stack are excluded from the comparison — chop is its
// own thing, not "the wrong side of trend".
//
// Fires when ≥3 against-stack trades AND ≥20pp WR gap below with-stack
// trades. Conservative thresholds — fighting an aligned stack isn't
// inherently wrong (failed-breakout fades, mean reversions), but a clear
// WR gap is actionable.

/**
 * Classify a trade's relationship to the EMA stack at its entry.
 * Returns 'with' | 'against' | 'neutral' (neutral = stack isn't aligned).
 *
 * Period-list-agnostic: whatever EMAs the user has configured (5/9/21/50/200,
 * or 8/13/21/55, or any other set ≥2 periods), the function reads them in
 * fastest→slowest order and checks for strict monotonic ordering. Bullish
 * stack = strictly descending in price (fast above slow); bearish = strictly
 * ascending. Anything else = neutral.
 *
 * Exported (named function) so the test suite can exercise it directly
 * without going through the full analyzer.
 */
const classifyEmaStack = (emasAtEntry, direction) => {
  if (!emasAtEntry || typeof emasAtEntry !== 'object') return 'neutral';

  // Sort periods present in ascending order (5 → 9 → 21 → 50 → 200, etc.).
  // String/number keys both supported (JSON round-trip turns numeric keys
  // into strings on load).
  const periods = Object.keys(emasAtEntry)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n) && n >= 1)
    .sort((a, b) => a - b);
  if (periods.length < 2) return 'neutral';

  const values = periods.map(p => emasAtEntry[p] ?? emasAtEntry[String(p)]);
  if (!values.every(v => Number.isFinite(v))) return 'neutral';

  // Bullish: each fast EMA above the next slower one (5 > 9 > 21 > 50 > 200).
  // Bearish: each fast below the next slower (5 < 9 < 21 < 50 < 200).
  let bullish = true;
  let bearish = true;
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] <= values[i + 1]) bullish = false;
    if (values[i] >= values[i + 1]) bearish = false;
  }
  if (!bullish && !bearish) return 'neutral';

  const isLong = direction !== 'short';
  if (bullish) return isLong ? 'with' : 'against';
  return isLong ? 'against' : 'with';
};

const detectStackAgainstGap = (day, trades) => {
  const classified = trades
    .map(t => {
      const a = ba(t);
      const stance = classifyEmaStack(a?.emasAtEntry, t.direction);
      return { trade: t, stance };
    });

  const withStack    = classified.filter(c => c.stance === 'with');
  const againstStack = classified.filter(c => c.stance === 'against');
  if (againstStack.length < STACK_MIN_TRADES) return null;
  if (withStack.length === 0) return null;

  const winRate = (group) => {
    if (group.length === 0) return 0;
    const wins = group.filter(c => c.trade.netPnl > 0).length;
    return wins / group.length;
  };
  const withWR    = winRate(withStack);
  const againstWR = winRate(againstStack);
  const gap = withWR - againstWR;
  if (gap < STACK_MIN_WR_GAP) return null;

  return makeObservation({
    id: `${key}:${day.date}:stack-against`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: 'Trades against EMA stack underperforming today',
    detail: `${againstStack.length} trades fought the EMA stack and hit ${fmtPct(againstWR)} WR vs ${fmtPct(withWR)} for ${withStack.length} stack-aligned trades.`,
    action: 'Stack alignment (9>21>50>200 or inverse) is the textbook trend-strength gauge. Fighting an aligned stack should be a deliberate counter-trend setup, not a default.',
    evidence: {
      againstStackTradeIds: againstStack.map(c => c.trade.id),
      withStackTradeIds: withStack.map(c => c.trade.id),
      againstWR,
      withWR,
      againstCount: againstStack.length,
      withCount: withStack.length,
    },
    confidence: 0.82,
    priority: 74
  });
};

// Export the classifier so tests can exercise it without driving the full
// analyzer. Not used elsewhere in the codebase.
export { classifyEmaStack };

// ── Pattern 8: EMA pullback recognition ──────────────────────────────────
//
// A textbook pullback entry is when, in an aligned stack, you buy/sell into
// the trend at a retracement near one of the EMAs (most commonly the 21).
// We detect these structurally:
//
//   - Stack must be aligned AND trade direction matches (with-stack)
//   - Entry price is within PULLBACK_MAX_DIST_R of one of the EMAs
//
// The chosen EMA is the closest one in R-distance. Reported as a SUCCESS
// observation with a count + the dominant EMA — positive reinforcement
// for setups the user is doing right.
//
// Risk in points (for converting EMA distance → R-distance) is derived
// from the trade's stopLoss when present. Trades without a usable stop
// are skipped — we can't measure proximity in R without a baseline R.

const detectEmaPullbacks = (day, trades) => {
  const pullbacks = [];

  for (const t of trades) {
    const a = ba(t);
    if (!a?.emasAtEntry) continue;
    if (classifyEmaStack(a.emasAtEntry, t.direction) !== 'with') continue;

    // Need risk distance to convert EMA distance into R
    const entry = Number(t.entry);
    const sl = Number(t.stopLoss);
    let riskPoints;
    if (Number.isFinite(sl) && sl > 0) {
      riskPoints = Math.abs(entry - sl);
    } else if (t.useStopLossPoints && Number.isFinite(Number(t.stopLossPoints))) {
      riskPoints = Math.abs(Number(t.stopLossPoints));
    } else {
      continue;
    }
    if (!riskPoints || riskPoints <= 0) continue;

    // Find the closest EMA to entry, in R-multiples. Walks whatever
    // periods the user has configured (no hardcoded list — supports any
    // chartSettings.emaPeriods set the user picks).
    let closestPeriod = null;
    let closestDistR = Infinity;
    const availablePeriods = Object.keys(a.emasAtEntry)
      .map(k => parseInt(k, 10))
      .filter(n => Number.isFinite(n) && n >= 1);
    for (const period of availablePeriods) {
      const ema = a.emasAtEntry[period] ?? a.emasAtEntry[String(period)];
      if (!Number.isFinite(ema)) continue;
      const distR = Math.abs(entry - ema) / riskPoints;
      if (distR < closestDistR) {
        closestDistR = distR;
        closestPeriod = period;
      }
    }
    if (closestPeriod === null) continue;
    if (closestDistR > PULLBACK_MAX_DIST_R) continue;

    pullbacks.push({ trade: t, period: closestPeriod, distR: closestDistR });
  }

  if (pullbacks.length < PULLBACK_MIN_TRADES) return null;

  // Most-frequent EMA period across the pullbacks (the "go-to pullback level")
  const periodCounts = {};
  for (const p of pullbacks) {
    periodCounts[p.period] = (periodCounts[p.period] || 0) + 1;
  }
  const dominantPeriod = Object.entries(periodCounts)
    .sort((a, b) => b[1] - a[1])[0][0];
  const dominantCount = periodCounts[dominantPeriod];

  // Win rate of pullback entries (positive reinforcement only when they work)
  const wins = pullbacks.filter(p => p.trade.netPnl > 0).length;
  const wr = wins / pullbacks.length;

  // Don't fire if pullbacks performed BADLY — the celebratory framing would be wrong.
  // Below 50% WR with a sample of ≥3, the user's "pullback entries" aren't actually
  // being recognized correctly; the data doesn't support the pattern as a strength.
  if (pullbacks.length >= 3 && wr < 0.5) return null;

  const allOnDominant = dominantCount === pullbacks.length;
  const dominantClause = allOnDominant
    ? `All on the ${dominantPeriod} EMA.`
    : `Most common: ${dominantCount}/${pullbacks.length} at the ${dominantPeriod} EMA.`;

  return makeObservation({
    id: `${key}:${day.date}:ema-pullback`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.EXECUTION,
    title: pullbacks.length === 1
      ? `Textbook pullback to ${dominantPeriod} EMA`
      : `${pullbacks.length} textbook pullback entries`,
    detail: pullbacks.length === 1
      ? `Entered within ${PULLBACK_MAX_DIST_R}R of the ${dominantPeriod} EMA in an aligned stack.`
      : `Each entered within ${PULLBACK_MAX_DIST_R}R of an EMA during stack alignment. ${dominantClause} ${fmtPct(wr)} WR.`,
    action: `Note what triggered the pull to the ${dominantPeriod} EMA — write the entry signal down so it's a checklist item, not improvisation.`,
    evidence: {
      pullbackTradeIds: pullbacks.map(p => p.trade.id),
      dominantPeriod,
      periodCounts,
      winRate: wr,
    },
    confidence: 0.8,
    priority: 60
  });
};

// ── Pattern 6: VWAP-misaligned WR gap ────────────────────────────────────
//
// Same classification logic as the 200 EMA pattern but anchored to
// session VWAP — the most-watched intraday futures reference. A long
// entry below VWAP is "VWAP-misaligned"; same for a short entry above
// VWAP. Fires when ≥3 misaligned trades AND their WR is ≥20pp lower than
// VWAP-aligned trades.

const detectVwapMisalignment = (day, trades) => {
  const classified = trades
    .map(t => {
      const a = ba(t);
      const vwap = a?.vwapAtEntry;
      if (!Number.isFinite(vwap)) return null;
      const entryPx = Number(t?.entry);
      if (!Number.isFinite(entryPx)) return null;
      const isLong = t.direction !== 'short';
      const aboveVwap = entryPx > vwap;
      const aligned = (isLong && aboveVwap) || (!isLong && !aboveVwap);
      return { trade: t, aligned, vwap, entryPx };
    })
    .filter(Boolean);

  if (classified.length === 0) return null;

  const aligned    = classified.filter(c => c.aligned);
  const misaligned = classified.filter(c => !c.aligned);
  if (misaligned.length < VWAP_MISALIGN_MIN_TRADES) return null;
  if (aligned.length === 0) return null;

  const winRate = (group) => {
    if (group.length === 0) return 0;
    const wins = group.filter(c => c.trade.netPnl > 0).length;
    return wins / group.length;
  };
  const alignedWR    = winRate(aligned);
  const misalignedWR = winRate(misaligned);
  const gap = alignedWR - misalignedWR;
  if (gap < VWAP_MISALIGN_MIN_WR_GAP) return null;

  return makeObservation({
    id: `${key}:${day.date}:vwap-misalign`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.SETUP,
    title: 'Trades against VWAP underperforming today',
    detail: `${misaligned.length} trades on the wrong side of VWAP hit ${fmtPct(misalignedWR)} WR vs ${fmtPct(alignedWR)} for ${aligned.length} VWAP-aligned trades.`,
    action: 'For futures intraday: VWAP is the institutional reference. Add a checklist line — "is my entry on the correct side of VWAP for my direction?"',
    evidence: {
      misalignedTradeIds: misaligned.map(c => c.trade.id),
      alignedTradeIds: aligned.map(c => c.trade.id),
      misalignedWR,
      alignedWR,
      misalignedCount: misaligned.length,
      alignedCount: aligned.length,
    },
    confidence: 0.82,
    priority: 73
  });
};

// ── Pattern 4: Wick stop-outs ─────────────────────────────────────────────
//
// A "wick stop-out" is a trade that was stopped out by a barely-past-1R move,
// when the trade also showed meaningful favorable movement. The signature:
//
//   - realized P&L is a loss (typically near −1R from a stop-out)
//   - MAE is *just* past 1R (1.00R–1.15R) — price tagged SL and didn't go far
//   - MFE was ≥0.5R — the trade had real legs at some point
//
// We don't try to verify the "and then it reversed in your favor" half (that
// would require post-exit bars, which barAnalysis doesn't include). The
// triple-condition above is already a strong "your stop got hunted" signal.

const detectWickStopouts = (day, trades) => {
  const wicked = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (t.netPnl >= 0) return false;
    if (!Number.isFinite(a.maeR) || !Number.isFinite(a.mfeR)) return false;
    if (a.maeR < WICK_MAE_R_MIN || a.maeR > WICK_MAE_R_MAX) return false;
    if (a.mfeR < WICK_MIN_MFE_R) return false;
    return true;
  });
  if (wicked.length === 0) return null;

  const total = trades.length;
  const worst = [...wicked].sort((a, b) => ba(b).mfeR - ba(a).mfeR)[0];
  const wba = ba(worst);

  const detail = wicked.length === 1
    ? `Stop hit at ${fmtR(-wba.maeR)} MAE — barely past 1R — but trade had ${fmtR(wba.mfeR)} of upside in the window.`
    : `${wicked.length} of ${total} losers tagged the stop within 15% of 1R, with meaningful upside before exit. Worst: ${fmtR(wba.mfeR)} of MFE on a ${fmtR(-wba.maeR)} MAE stop-out.`;

  return makeObservation({
    id: `${key}:${day.date}:wick-stopout`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.WARNING,
    category: CATEGORY.RISK,
    title: wicked.length === 1
      ? 'Wick stop-out — barely past 1R'
      : `${wicked.length} wick stop-outs today`,
    detail,
    action: 'Check whether your SL placement is at the actual liquidity level (under/over the swing) or just N ticks from entry. Tight stops get hunted.',
    evidence: {
      wickedTradeIds: wicked.map(t => t.id),
      worstTradeId: worst.id,
      worstMaeR: wba.maeR,
      worstMfeR: wba.mfeR,
    },
    confidence: 0.8,
    priority: 72
  });
};

// ── Pattern 3: Sharp entries (positive reinforcement) ─────────────────────

const detectSharpEntries = (day, trades) => {
  const sharp = trades.filter(t => {
    const a = ba(t);
    if (!a) return false;
    if (!Number.isFinite(a.maeR) || !Number.isFinite(a.mfeR)) return false;
    return a.maeR <= SHARP_ENTRY_MAX_MAE_R && a.mfeR >= SHARP_ENTRY_MIN_MFE_R;
  });
  if (sharp.length === 0) return null;

  // Headline: best one (highest MFE-to-MAE ratio)
  const best = [...sharp].sort((a, b) => {
    const ra = ba(b).mfeR / Math.max(ba(b).maeR, 0.01);
    const rb = ba(a).mfeR / Math.max(ba(a).maeR, 0.01);
    return ra - rb;
  })[0];
  const bba = ba(best);

  // Find common factor (setup name) for actionable next step
  const setups = sharp.map(t => t.setup).filter(Boolean);
  const commonSetup = setups.length > 0
    ? setups.reduce((a, b, _, arr) => arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b)
    : null;
  const commonClause = commonSetup && setups.filter(s => s === commonSetup).length >= 2
    ? ` Common setup: "${commonSetup}".`
    : '';

  return makeObservation({
    id: `${key}:${day.date}:sharp-entry`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.EXECUTION,
    title: sharp.length === 1
      ? 'Sharp entry — barely took heat'
      : `${sharp.length} sharp entries today`,
    detail: sharp.length === 1
      ? `Trade barely went against you (${fmtR(bba.maeR)} MAE) and ran ${fmtR(bba.mfeR)}.`
      : `Each had under ${SHARP_ENTRY_MAX_MAE_R}R MAE and over ${SHARP_ENTRY_MIN_MFE_R}R MFE.${commonClause}`,
    action: commonSetup && commonClause
      ? `Tag what's working about your "${commonSetup}" entries — checklist item for tomorrow.`
      : 'Note what you saw on these entries — write the pattern down while it\'s fresh.',
    evidence: {
      sharpTradeIds: sharp.map(t => t.id),
      bestTradeId: best.id,
      bestMaeR: bba.maeR,
      bestMfeR: bba.mfeR,
      commonSetup
    },
    confidence: 0.85,
    priority: 55
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { day } = ctx || {};
  if (!day) return null;

  // DayContext exposes the enriched trade list as `day.trades` (with
  // netPnl already populated by buildDayContext). The earlier shape
  // assumed `day.enriched` which doesn't exist on the public context —
  // the analyzer was silently emitting nothing in production until
  // this fix. We still accept `day.enriched` because the contract
  // tests use that shape — picking whichever is non-empty keeps both
  // paths green.
  const sourceTrades = Array.isArray(day.trades) && day.trades.length > 0
    ? day.trades
    : day.enriched;
  const trades = usableTrades(sourceTrades);
  if (trades.length === 0) return null;

  const observations = [];

  const reversed = detectReversed(day, trades);
  if (reversed) observations.push(reversed);

  const wicked = detectWickStopouts(day, trades);
  if (wicked) observations.push(wicked);

  const countertrend = detectCountertrendGap(day, trades);
  if (countertrend) observations.push(countertrend);

  const vwapMisalign = detectVwapMisalignment(day, trades);
  if (vwapMisalign) observations.push(vwapMisalign);

  const stackAgainst = detectStackAgainstGap(day, trades);
  if (stackAgainst) observations.push(stackAgainst);

  const pullbacks = detectEmaPullbacks(day, trades);
  if (pullbacks) observations.push(pullbacks);

  const leakage = detectMfeLeakage(day, trades);
  if (leakage) observations.push(leakage);

  const sharp = detectSharpEntries(day, trades);
  if (sharp) observations.push(sharp);

  return observations.length === 0 ? null : observations;
};
